import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import api from '../../lib/api';
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { SkillsSelector } from '../../components/profile/SkillsSelector';
import AnonymousResumeUploadCard from '../../components/AnonymousResumeUploadCard';
import { WORKER_SKILLS, YEARS_OF_EXPERIENCE } from '../../lib/constants';
import PesoLayout from './PesoLayout';
import TopBar from '../../components/TopBar';
import { Field, Txt, Sel, RadioRow, CheckGroup, SectionCard } from './PesoFormControls';
import { pesoSignUp } from './pesoSignup';
import { useOffice } from './officeCtxRef';
import { BOHOL_MUNICIPALITIES, getBarangaysForMunicipality } from '../../data/boholLocations';
import {
  NSRP_SEX, NSRP_CIVIL_STATUS, NSRP_DISABILITIES, NSRP_EMPLOYMENT_STATUS,
  NSRP_SEPARATION_REASONS, NSRP_EMPLOYMENT_TYPE_PREF,
  NSRP_EDUCATION_LEVELS, SENIOR_HIGH_STRANDS, NSRP_NC_CERTS,
  NSRP_APPOINTMENT_STATUS, NSRP_OTHER_SKILLS, NSRP_TO_BT_EDUCATION, NSRP_HIGHEST_TO_BT,
  NSRP_LANG_CERTS, NSRP_DIALECTS, NSRP_21C_SKILLS, NSRP_HIGHEST_EDUCATION,
} from './pesoFields';

const emptyEdu = () => ({ level: '', strand: '', course: '', year_graduated: '', year_last: '', school: '', awards: '' });
const emptyTraining = () => ({ course: '', institution: '', hours: '', cert: '', date_from: '', date_to: '', completed: '' });
const emptyWork = () => ({ company: '', position: '', appointment_status: '', date_from: '', date_to: '', city: '' });

// ---- Resume → NSRP field mappers ---------------------------------------------
// A resume rarely maps 1:1 onto the NSRP form, so these translate free-text
// resume output into the exact NSRP option values / row shapes. All best-effort:
// return '' / [] when nothing is confidently derivable (never guess).

// Age derived read-only from the seeker's date of birth (NSRP §I). Returns '' when the
// DOB is blank/invalid so the read-only Age field stays empty rather than showing NaN.
const ageFromDob = (dob) => {
  if (!dob) return '';
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 130 ? String(age) : '';
};

// Minimum working age enforced platform-wide (matches ProfileCompletion.js + the
// PUT /api/profile age check, both 16). The DOB picker is capped at today - 16 years
// and handleSubmit re-checks, so a seeker can't register with a birth date that makes
// them under 16 (previously today's date was accepted).
const MIN_WORK_AGE = 16;
const maxDobForMinAge = () =>
  new Date(new Date().setFullYear(new Date().getFullYear() - MIN_WORK_AGE)).toISOString().split('T')[0];

// "yyyy-mm" (or "yyyy-mm-dd") -> "mm/yyyy" for the NSRP §VI Inclusive Dates column.
const mmYyyy = (v) => {
  if (!v) return '';
  const m = String(v).match(/(\d{4})-(\d{2})/);
  return m ? `${m[2]}/${m[1]}` : String(v);
};

// Resume duration string ("2019 - 2022" / "Jan 2020 - Present") -> NSRP §VI Inclusive
// Dates {from,to} as "yyyy-mm" (the value shape a <input type="month"> uses).
// Best-effort: return '' when nothing is confidently derivable (never guess).
const durationToDates = (dur) => {
  if (!dur) return { from: '', to: '' };
  const s = String(dur);
  const years = s.match(/(?:19|20)\d{2}/g);
  if (!years || !years.length) return { from: '', to: '' };
  const start = years[0];
  const end = /present|current|now|ongoing/i.test(s)
    ? String(new Date().getFullYear())
    : (years.length >= 2 ? years[years.length - 1] : '');
  return { from: `${start}-01`, to: end ? `${end}-01` : '' };
};

// Bohol Trabaho education_level enum -> NSRP §III "Highest Educational Level" value.
const BT_TO_NSRP_HIGHEST = {
  elementary: 'elem_grad', high_school: 'hs_grad', vocational: 'tvet',
  college_level: 'col_level', college_graduate: 'col_grad', bachelors: 'col_grad',
  masters: 'post_grad', doctorate: 'post_grad',
};

// Free-text education level (from the resume) -> NSRP §V education-row level value.
const eduLevelToNsrp = (lvl) => {
  const k = String(lvl || '').toLowerCase().replace(/\s+/g, '_');
  if (/graduate_studies|master|doctor|phd|post_grad/.test(k)) return 'graduate_studies';
  if (/senior_high|shs/.test(k)) return 'senior_high';
  if (/college|tertiary|university|bachelor|vocational|tvet|diploma/.test(k)) return 'tertiary';
  if (/high_school|secondary|junior_high/.test(k)) return 'secondary_k12';
  if (/elementary|primary/.test(k)) return 'elementary';
  return '';
};

// Match free-text resume skills to the fixed NSRP §VIII "Technical Skills Acquired
// Without Formal Training" checklist. Every emitted label MUST be in NSRP_OTHER_SKILLS
// (case-identical) and MUST NOT be 'Others' — a résumé cannot supply the "specify" text,
// so an auto-checked "Others" box would print blank.
const OTHER_SKILL_PATTERNS = [
  ['Auto Mechanic', /auto\s*mechanic|mechanic/i], ['Carpentry', /carpentry|carpenter/i],
  ['Welding', /weld/i], ['Driving', /driver|driving/i], ['Gardening', /garden|landscap/i],
  ['Masonry', /masonry|mason|concrete/i], ['Photography', /photograph/i], ['Plumbing', /plumb/i],
  ['Hairdressing', /beautician|cosmetolog|hairdress|barber/i], ['Tailoring', /tailor|sewing|dressmak|embroider/i],
  ['Cooking', /\bcook|culinary|chef/i], ['Baking', /bak(e|ing)|pastry/i],
];
const matchOtherSkills = (skills) => {
  const joined = (skills || []).join(' ; ');
  const out = [];
  for (const [label, re] of OTHER_SKILL_PATTERNS) if (re.test(joined) && !out.includes(label)) out.push(label);
  return out;
};

// Dev-only, non-throwing drift guard: every OTHER_SKILL_PATTERNS label must map to a real
// NSRP_OTHER_SKILLS checkbox and never be 'Others'. Never throw at module load on a user route.
if (process.env.NODE_ENV !== 'production') {
  const bad = OTHER_SKILL_PATTERNS.map((p) => p[0]).filter((l) => !NSRP_OTHER_SKILLS.includes(l) || l === 'Others');
  if (bad.length) console.error('matchOtherSkills drift:', bad);
}

// Match free-text dialects to the NSRP §IV checklist (Cebuano/Bisaya -> "Others").
const matchDialects = (list) => {
  const joined = (list || []).join(' ; ');
  const out = [];
  for (const o of ['Tagalog', 'Ilocano', 'Ilonggo', 'Bikol']) if (new RegExp(o, 'i').test(joined)) out.push(o);
  const cebuano = /cebuano|bisaya|binisaya|boholano|visayan/i.test(joined);
  if (cebuano) out.push('Others');
  return { list: out, cebuano };
};

const PesoSeekerRegister = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const office = useOffice();
  const [searchParams] = useSearchParams();
  const [eventInfo, setEventInfo] = useState(null); // { valid, code, name } | null when the ?event= link is valid
  useEffect(() => {
    const code = (searchParams.get('event') || '').trim().toLowerCase();
    const slug = office?.pesoOffice || office?.slug;
    if (!code || !slug) { setEventInfo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/peso/event-info', { params: { office: slug, code } });
        if (!cancelled) setEventInfo(res.data?.valid ? res.data : null);
      } catch { if (!cancelled) setEventInfo(null); }
    })();
    return () => { cancelled = true; };
  }, [searchParams, office]);
  const [submitting, setSubmitting] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeExtras, setResumeExtras] = useState({}); // tagline, education_field, work_history

  const [f, setF] = useState({
    email: '', password: '',
    surname: '', first_name: '', middle_name: '', suffix: '',
    date_of_birth: '', sex: '', place_of_birth: '', citizenship: 'Filipino',
    address_house: '', barangay: '', municipality: '', province: 'Bohol',
    outside_lgu: false, // present-address: seeker lives outside the host LGU (SRA/job-fair applicants)
    perm_same: false, perm_address_house: '', perm_barangay: '', perm_municipality: '', perm_province: '',
    civil_status: '', civil_status_other: '', height: '', weight: '', disability: [], disability_other: '',
    phone: '', landline: '', mobile_secondary: '',
    employment_status: '', actively_looking: '',
    ofw: 'no', considering_return: '', fourps: '', fourps_household_id: '',
    job_search_months: '', separation_reasons: [], terminated_abroad_country: '',
    willing_immediately: '', willing_when: '',
    pref_occupation_1: '', pref_occupation_2: '', pref_occupation_3: '',
    pref_industry_1: '', pref_industry_2: '', pref_industry_3: '', pref_employment_type: '',
    pref_local_1: '', pref_local_2: '', pref_local_3: '', pref_overseas_1: '', pref_overseas_2: '', pref_overseas_3: '',
    salary_expectation: '',
    lang_certs: [], lang_cert_other: '', lang_validity: '', dialects: [], dialect_other: '',
    currently_in_school: 'no', currently_in_training: '', highest_education: '',
    education_rows: [emptyEdu()], training_rows: [emptyTraining()],
    civil_service_eligibility: '', eligibility_date: '', prc_license: '', prc_valid_until: '',
    work_rows: [emptyWork()],
    years_of_experience: '', skills: [], skills_21: [], other_skills: [], other_skills_other: '',
    consent: false,
  });
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  // Default the address municipality to this LGU office (editable), once office loads.
  useEffect(() => {
    if (office?.municipality) {
      setF((prev) => (prev.municipality ? prev : { ...prev, municipality: office.municipality }));
    }
  }, [office?.municipality]);

  // Pre-fill the NSRP form from a parsed resume. Every mapping is fill-if-empty so
  // it never clobbers something the seeker already typed. Fills not just the profile
  // basics but the NSRP sections a resume covers (work experience, job preference,
  // §IX skills, education rows, dialects) so the printed Form 1 isn't half-blank.
  const onParsed = (x) => {
    setF((prev) => {
      const next = { ...prev };
      // --- Identity + contact ---
      if (x.name && !prev.surname && !prev.first_name) {
        const parts = String(x.name).trim().split(/\s+/);
        if (parts.length === 1) next.first_name = parts[0];
        else { next.surname = parts[parts.length - 1]; next.first_name = parts.slice(0, -1).join(' '); }
      }
      if (x.email && !prev.email) next.email = x.email;
      if (x.phone && !prev.phone) next.phone = x.phone;
      // Fill-if-empty like every other mapping: keep the office's default municipality
      // (and its barangay Select) instead of letting a resume's town flip it to free text.
      if (x.town && !prev.municipality) next.municipality = x.town;
      if (x.barangay && !prev.barangay) next.barangay = x.barangay;
      // province intentionally not mapped: it defaults to 'Bohol' (every PESO office is in
      // Bohol), so a resume value should never override it.
      if (x.sex && !prev.sex && ['male', 'female'].includes(String(x.sex).toLowerCase())) next.sex = String(x.sex).toLowerCase();
      if (x.civil_status && !prev.civil_status) {
        const cs = String(x.civil_status).toLowerCase();
        if (['single', 'married', 'widowed', 'separated'].includes(cs)) next.civil_status = cs;
      }

      // --- Experience + skills ---
      if (x.years_of_experience && !prev.years_of_experience) next.years_of_experience = x.years_of_experience;
      if (Array.isArray(x.skills) && x.skills.length && !prev.skills.length) next.skills = x.skills.slice(0, 12);
      if (Array.isArray(x.skills) && x.skills.length && !prev.other_skills.length) {
        const matched = matchOtherSkills(x.skills);        // NSRP §VIII technical checkboxes
        // Belt-and-suspenders: only keep labels with a real checkbox, never 'Others'
        // (a résumé can't supply the "specify" text → an auto-ticked Others prints blank).
        const filtered = matched.filter((s) => NSRP_OTHER_SKILLS.includes(s) && s !== 'Others');
        if (filtered.length) next.other_skills = filtered;
      }
      if (Array.isArray(x.dialects) && x.dialects.length && !prev.dialects.length) {
        const { list: dl, cebuano } = matchDialects(x.dialects);   // NSRP §IV dialects
        if (dl.length) next.dialects = dl;
        if (cebuano && !prev.dialect_other) next.dialect_other = 'Cebuano';
      }

      // --- Job preference (§III): seed the first preferred occupation/industry ---
      if (!prev.pref_occupation_1) {
        const firstRole = Array.isArray(x.work_history) && x.work_history[0] ? x.work_history[0].position : '';
        const desired = x.desired_position || firstRole || '';
        if (desired) next.pref_occupation_1 = String(desired).slice(0, 60);
      }
      if (!prev.pref_industry_1 && x.desired_industry) next.pref_industry_1 = String(x.desired_industry).slice(0, 40);

      // --- Education (§III highest + §V rows) ---
      if (x.education_level && !prev.highest_education) {
        const h = BT_TO_NSRP_HIGHEST[String(x.education_level).toLowerCase()];
        if (h) next.highest_education = h;
      }
      const eduRowsEmpty = prev.education_rows.every((r) => !r.level && !r.school && !r.course && !r.year_graduated);
      const eduList = Array.isArray(x.education)
        ? x.education.filter((e) => e && (e.school || e.course || e.level || e.year_graduated)) : [];
      if (eduList.length && eduRowsEmpty) {
        // The resume lists education most-recent-first, but the NSRP §V "most recent
        // education record" (and the submit's education_level derivation) both read the
        // LAST row as the highest/most recent — so reverse to newest-last.
        next.education_rows = eduList.slice(0, 4).reverse().map((e) => ({
          ...emptyEdu(),
          level: eduLevelToNsrp(e.level),
          school: e.school || '',
          course: e.course || '',
          year_graduated: e.year_graduated ? String(e.year_graduated).replace(/\D/g, '').slice(0, 4) : '',
        }));
      } else if (x.education_field && eduRowsEmpty) {
        next.education_rows = [{ ...emptyEdu(), course: x.education_field }, ...prev.education_rows.slice(1)];
      }

      // --- Work experience (§VIII) ---
      const wh = Array.isArray(x.work_history) ? x.work_history.filter((w) => w && (w.company || w.position)) : [];
      const workRowsEmpty = prev.work_rows.every((r) => !r.company && !r.position);
      if (wh.length && workRowsEmpty) {
        next.work_rows = wh.slice(0, 5).map((w) => {
          const d = durationToDates(w.duration);
          return {
            ...emptyWork(),
            company: w.company || '',
            position: w.position || '',
            city: w.location || '',
            date_from: d.from,
            date_to: d.to,
          };
        });
      }
      return next;
    });
    setResumeExtras({
      tagline: x.tagline || '',
      education_field: x.education_field || '',
      education_level_bt: x.education_level || '',
      work_history: Array.isArray(x.work_history) ? x.work_history.slice(0, 5) : [],
    });
  };

  // ---- repeatable-row helpers ----
  const addRow = (key, factory) => set(key, [...f[key], factory()]);
  const rmRow = (key, idx) => set(key, f[key].filter((_, i) => i !== idx));
  const setRow = (key, idx, field, val) =>
    set(key, f[key].map((r, i) => (i === idx ? { ...r, [field]: val } : r)));

  // Present-address "I live outside {office town}" toggle. When on, Municipality + Barangay
  // become the all-Bohol cascading dropdowns (same data/logic as the regular profile); when off,
  // they revert to the host office's town + barangay list. Barangay is reset on both transitions
  // so a stale selection from the other mode can't survive.
  const toggleOutside = (checked) => {
    setF((prev) => ({
      ...prev,
      outside_lgu: checked,
      municipality: checked ? '' : (office?.municipality || ''),
      barangay: '',
    }));
  };

  // All-Bohol municipality pick (only used while outside_lgu is on): set the town and drop the
  // barangay if it isn't one of the newly-selected municipality's barangays (mirrors ProfileNew).
  const setOutsideMunicipality = (v) => {
    setF((prev) => ({
      ...prev,
      municipality: v,
      barangay: getBarangaysForMunicipality(v).includes(prev.barangay) ? prev.barangay : '',
    }));
  };

  // Mirror present address into permanent when "same as present" is ticked.
  const togglePermSame = (checked) => {
    setF((prev) => ({
      ...prev, perm_same: checked,
      perm_address_house: checked ? prev.address_house : prev.perm_address_house,
      perm_barangay: checked ? prev.barangay : prev.perm_barangay,
      perm_municipality: checked ? prev.municipality : prev.perm_municipality,
      perm_province: checked ? prev.province : prev.perm_province,
    }));
  };

  const handleSubmit = async () => {
    // Single-page form: validate the required fields up front (account + key personal + consent),
    // surfacing the first missing group with a clear message instead of step-gating.
    // Email is OPTIONAL — the phone number is the fallback login. If an email IS given,
    // it must be valid; the phone number itself is validated in the personal-info block below.
    if (f.email.trim() && !/\S+@\S+\.\S+/.test(f.email.trim())) {
      toast.error(t('peso.invalidEmail', 'Please enter a valid email address, or leave it blank to use your mobile number instead.'));
      return;
    }
    if (f.password.length < 8) {
      toast.error(t('peso.fillPassword', 'Please set a password of at least 8 characters.'));
      return;
    }
    if (!(f.surname && f.first_name && f.date_of_birth && f.sex && f.address_house &&
          f.barangay && f.municipality && f.province && f.civil_status && f.phone &&
          f.place_of_birth && f.height && f.weight)) {
      toast.error(t('peso.fillPersonal', 'Please complete your personal information: name, birth date, sex, place of birth, address, civil status, height, weight, and mobile number.'));
      return;
    }
    // Enforce the minimum working age (16, platform standard). The DOB picker is capped,
    // but re-check here so a typed/pasted date can't bypass it (and the /api/profile enrich
    // would otherwise 400 on age < 16 and silently drop the typed columns).
    const seekerAge = parseInt(ageFromDob(f.date_of_birth), 10);
    if (!Number.isFinite(seekerAge) || seekerAge < MIN_WORK_AGE) {
      toast.error(t('peso.minAge', `You must be at least ${MIN_WORK_AGE} years old to register for work.`));
      return;
    }
    if (!f.employment_status) {
      toast.error(t('peso.fillEmpStatus', 'Please select your Employment Status (Section I).'));
      return;
    }
    if (!f.actively_looking) {
      toast.error(t('peso.fillActivelyLooking', 'Please answer whether you are actively looking for work (Section I).'));
      return;
    }
    // Education level + years of experience drive job matching AND are the profile's
    // completion-gate fields — require them here so the seeker lands on a complete
    // profile (and is never bounced to a separate "complete your profile" page).
    if (!f.highest_education) {
      toast.error(t('peso.fillHighestEdu', 'Please select your Highest Educational Level (Section III).'));
      return;
    }
    if (!f.years_of_experience) {
      toast.error(t('peso.fillYoe', 'Please select your Total Years of Experience (Section VI).'));
      return;
    }
    if (f.skills_21.length !== 5) {
      toast.error(t('peso.fill21c', 'Please select exactly five 21st Century Skills (Section VII).'));
      return;
    }
    if (f.other_skills.length < 1) {
      toast.error(t('peso.fillTechSkills', 'Please select at least one Technical Skill (Section VIII).'));
      return;
    }
    if (!f.consent) { toast.error(t('peso.consentRequired', 'Please confirm the certification & consent.')); return; }
    setSubmitting(true);
    try {
      const fullName = [f.first_name, f.middle_name, f.surname, f.suffix].filter(Boolean).join(' ').trim();

      // Derive BT education_level from the highest NSRP education row, fallback to resume
      const levels = f.education_rows.map((r) => r.level).filter(Boolean);
      const topLevel = levels[levels.length - 1];
      // Prefer the §V education-row level, then the §III highest-education select (always
      // present once required), then any resume-derived value. Guarantees education_level.
      const educationLevelBt = (topLevel && NSRP_TO_BT_EDUCATION[topLevel])
        || NSRP_HIGHEST_TO_BT[f.highest_education]
        || resumeExtras.education_level_bt || null;

      // work_history typed column (for marketplace matching), fallback to resume.
      // Normalize EVERY entry to the keys the backend validator requires
      // (company/position/duration) — raw resume entries may omit one, which would
      // otherwise make the whole profile PUT 400 and silently drop all typed fields.
      const normalizeWork = (rows) => (rows || [])
        .filter((w) => w && (w.company || w.position))
        .map((w) => ({
          company: w.company || '', position: w.position || '',
          duration: w.duration || '', description: w.description || '',
        }))
        .slice(0, 5);
      const workHistoryTyped = normalizeWork(f.work_rows.filter((r) => r.company || r.position).map((r) => ({
        company: r.company || '', position: r.position || '',
        duration: (r.date_from || r.date_to) ? `${mmYyyy(r.date_from)} - ${mmYyyy(r.date_to)}`.trim() : '', description: '',
      })));
      const finalWorkHistory = workHistoryTyped.length ? workHistoryTyped : normalizeWork(resumeExtras.work_history);

      const preferredLocations = [f.pref_local_1, f.pref_local_2, f.pref_local_3].filter(Boolean).slice(0, 5);

      // Full NSRP payload (everything not in typed columns) — 1:1 with NSRP Form 1.REV 3
      const nsrp_data = {
        personal: {
          surname: f.surname, first_name: f.first_name, middle_name: f.middle_name, suffix: f.suffix,
          sex: f.sex, place_of_birth: f.place_of_birth, citizenship: f.citizenship,
          address_house: f.address_house, barangay: f.barangay, municipality: f.municipality, province: f.province,
          perm_same: f.perm_same, perm_address_house: f.perm_address_house, perm_barangay: f.perm_barangay,
          perm_municipality: f.perm_municipality, perm_province: f.perm_province,
          civil_status: f.civil_status, civil_status_other: f.civil_status_other,
          height: f.height, weight: f.weight, landline: f.landline, mobile_secondary: f.mobile_secondary,
          disability: f.disability, disability_other: f.disability_other,
        },
        employment: {
          status: f.employment_status,
          ofw: f.ofw, considering_return: f.considering_return, fourps: f.fourps, fourps_household_id: f.fourps_household_id,
          job_search_months: f.job_search_months, actively_looking: f.actively_looking,
          separation_reasons: f.separation_reasons, terminated_abroad_country: f.terminated_abroad_country,
          willing_immediately: f.willing_immediately, willing_when: f.willing_when,
        },
        preferences: {
          // kept unfiltered (3 slots) so occupation[i] stays paired with industry[i] on the print form
          occupations: [f.pref_occupation_1, f.pref_occupation_2, f.pref_occupation_3],
          industries: [f.pref_industry_1, f.pref_industry_2, f.pref_industry_3],
          employment_type: f.pref_employment_type,
          local: [f.pref_local_1, f.pref_local_2, f.pref_local_3].filter(Boolean),
          overseas: [f.pref_overseas_1, f.pref_overseas_2, f.pref_overseas_3].filter(Boolean),
          salary_expectation: f.salary_expectation,
        },
        language: { certs: f.lang_certs, cert_other: f.lang_cert_other, validity: f.lang_validity },
        dialects: { list: f.dialects, other: f.dialect_other },
        skills_21: f.skills_21,
        currently_in_school: f.currently_in_school,
        currently_in_training: f.currently_in_training,
        highest_education: f.highest_education,
        education_rows: f.education_rows.filter((r) => r.level || r.course || r.school),
        training_rows: f.training_rows.filter((r) => r.course || r.institution),
        eligibility: {
          civil_service: f.civil_service_eligibility, eligibility_date: f.eligibility_date,
          prc_license: f.prc_license, prc_valid_until: f.prc_valid_until,
        },
        work_experience: f.work_rows.filter((r) => r.company || r.position),
        other_skills: f.other_skills, other_skills_other: f.other_skills_other,
        form: 'nsrp_form_1', captured_at: new Date().toISOString(),
      };

      const metadata = {
        name: fullName || 'Job Seeker',
        role: 'job_seeker',
        town: f.municipality || office?.municipality || '',
        phone: f.phone,
        years_of_experience: f.years_of_experience || undefined,
        education_level: educationLevelBt || undefined,
        date_of_birth: f.date_of_birth || undefined,
        referred_by: office?.referralCode,
        terms_agreed_at: new Date().toISOString(),
        registration_source: office?.registrationSource,
        peso_office: office?.pesoOffice,
        event_code: eventInfo?.code || undefined,
        nsrp_data: JSON.stringify(nsrp_data),
      };

      const typedProfile = {
        skills: f.skills,
        work_history: finalWorkHistory,
        barangay: f.barangay,
        preferred_locations: preferredLocations,
        nsrp_data,
      };
      if (f.years_of_experience) typedProfile.years_of_experience = f.years_of_experience;
      if (educationLevelBt) typedProfile.education_level = educationLevelBt;
      if (f.date_of_birth) typedProfile.date_of_birth = f.date_of_birth;
      if (resumeExtras.education_field) typedProfile.education_field = resumeExtras.education_field;
      if (resumeExtras.tagline && resumeExtras.tagline.length <= 80) typedProfile.tagline = resumeExtras.tagline;

      // Map NSRP fields the form already collects into typed profile columns, so the seeker
      // lands with a rich, high-completeness profile instead of this data being buried in nsrp_data.
      const salaryNum = parseInt(f.salary_expectation, 10);
      // Cap at the backend salary ceiling (le=10,000,000 in UpdateWorkerProfileRequest); an
      // out-of-range value would 400 the whole /api/profile PUT and silently drop ALL typed columns.
      if (Number.isFinite(salaryNum) && salaryNum > 0 && salaryNum <= 10000000) {
        // A salary expectation is a single figure; set min = max so the row satisfies the
        // profiles chk_salary_range constraint (max >= min). Setting salary_min alone makes
        // the DB reject the whole /api/profile update, silently dropping ALL typed columns.
        typedProfile.salary_min = salaryNum;
        typedProfile.salary_max = salaryNum;
        typedProfile.salary_expectation_type = 'monthly';
      }
      if (f.pref_employment_type) typedProfile.preferred_work_types = [f.pref_employment_type];
      if (f.willing_immediately === 'yes') typedProfile.availability = ['immediate'];
      if (!typedProfile.education_field) {
        const topEdu = [...f.education_rows].reverse().find((r) => r.course || r.strand);
        if (topEdu) typedProfile.education_field = topEdu.course || topEdu.strand;
      }

      const { needsEmailVerification } = await pesoSignUp({
        email: f.email, password: f.password, phone: f.phone, metadata, typedProfile, resumeFile,
      });

      try { localStorage.removeItem('referral_code'); } catch {}

      if (needsEmailVerification) {
        toast.success(t('peso.checkEmail', 'Account created! Please check your email to confirm.'));
        navigate(`/check-email?email=${encodeURIComponent(f.email)}`);
      } else {
        toast.success(t('peso.registered', 'Registration complete! Welcome to Bohol Trabaho.'));
        // Soft SPA navigation (NOT a hard window.location reload): a hard reload right after
        // signup drops the just-created session inside mobile in-app WebViews (Facebook/Safari),
        // booting the user out to the login page. Staying in the SPA keeps the in-memory session;
        // App.js already has the signed-in user (onAuthStateChange) and self-heals the profile.
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      const msg = err?.message || err?.response?.data?.detail || t('peso.signupFailed', 'Registration failed. Please try again.');
      toast.error(typeof msg === 'string' ? msg : 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <TopBar />
      <PesoLayout>
        <Helmet><title>NSRP Job Seeker Registration | PESO Panglao</title></Helmet>

        <div className="mb-5">
          <button onClick={() => navigate(`/peso/${office?.slug || 'panglao'}`)} className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
            <ArrowLeft size={14} /> {t('peso.backToPortal', 'Back to portal')}
          </button>
          <h1 className="text-xl font-bold text-slate-900">{t('peso.seekerRegTitle', 'NSRP Job Seeker Registration')}</h1>
          <p className="text-sm text-slate-500">{t('peso.seekerRegSubtitle', 'Complete the form below and submit. Fields marked * are required; the rest help us match you to jobs.')}</p>
        </div>

        {eventInfo?.valid && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            {t('peso.registeringForEvent', 'Registering for')}: <span className="font-semibold">{eventInfo.name}</span>
          </div>
        )}

        {/* Single-page registration — all sections visible, one submit at the bottom */}
        <div className="space-y-4">
          {/* Resume + account */}
          <AnonymousResumeUploadCard onParsed={onParsed} onFileSelected={setResumeFile} />
          <SectionCard title={t('peso.accountTitle', 'Create your account')}>
            <p className="-mt-1 text-xs text-slate-500">
              {t('peso.accountIntro', 'Your mobile number is your sign-in. Add an email only if you have one — it is optional.')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={t('peso.contact', 'Mobile Number (Primary number)')} required hint={t('peso.mobileLoginHint', 'This is your sign-in and appears on your NSRP form. Format: 09XXXXXXXXX')}>
                <Txt value={f.phone} onChange={(v) => set('phone', v)} placeholder="09XXXXXXXXX" />
              </Field>
              <Field label={t('peso.emailOptional', 'Email Address (optional)')} hint={t('peso.emailOptionalHint', 'Only if you have one — otherwise your mobile number is your account.')}>
                <Txt type="email" value={f.email} onChange={(v) => set('email', v)} />
              </Field>
              <Field label={t('peso.password', 'Password')} required hint={t('peso.passwordHint', 'At least 8 characters')}>
                <Txt type="password" value={f.password} onChange={(v) => set('password', v)} />
              </Field>
            </div>
          </SectionCard>

          {/* I. Personal Information */}
          <SectionCard title={t('peso.personalTitle', 'I. Personal Information')}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={t('peso.lastName', 'Last Name')} required><Txt value={f.surname} onChange={(v) => set('surname', v)} /></Field>
              <Field label={t('peso.firstName', 'First Name')} required><Txt value={f.first_name} onChange={(v) => set('first_name', v)} /></Field>
              <Field label={t('peso.middleName', 'Middle Name')}><Txt value={f.middle_name} onChange={(v) => set('middle_name', v)} /></Field>
              <Field label={t('peso.suffix', 'Suffix (Sr., Jr.)')}><Txt value={f.suffix} onChange={(v) => set('suffix', v)} /></Field>
              <Field label={t('peso.dob', 'Date of Birth (mm/dd/yyyy)')} required hint={t('peso.dobHint', `Must be at least ${MIN_WORK_AGE} years old`)}><Txt type="date" value={f.date_of_birth} onChange={(v) => set('date_of_birth', v)} min="1925-01-01" max={maxDobForMinAge()} /></Field>
              <Field label={t('peso.age', 'Age')}><Txt value={ageFromDob(f.date_of_birth)} onChange={() => {}} readOnly disabled /></Field>
              <Field label={t('peso.sex', 'Sex')} required><RadioRow name="sex" value={f.sex} onChange={(v) => set('sex', v)} options={NSRP_SEX} /></Field>
              <Field label={t('peso.placeOfBirth', 'Place of Birth')} required><Txt value={f.place_of_birth} onChange={(v) => set('place_of_birth', v)} /></Field>
              <Field label={t('peso.citizenship', 'Citizenship')}><Txt value={f.citizenship} onChange={(v) => set('citizenship', v)} /></Field>
              <Field label={t('peso.civilStatus', 'Civil Status')} required><Sel value={f.civil_status} onChange={(v) => set('civil_status', v)} options={NSRP_CIVIL_STATUS} /></Field>
              {f.civil_status === 'others' && (
                <Field label={t('peso.civilStatusOther', 'Civil status (specify)')}><Txt value={f.civil_status_other} onChange={(v) => set('civil_status_other', v)} /></Field>
              )}
              <Field label={t('peso.mobileSecondary', 'Mobile Number (Secondary)')}><Txt value={f.mobile_secondary} onChange={(v) => set('mobile_secondary', v)} /></Field>
              <Field label={t('peso.landline', 'Landline Number')}><Txt value={f.landline} onChange={(v) => set('landline', v)} /></Field>
              <Field label={t('peso.height', 'Height (cm)')} required><Txt value={f.height} onChange={(v) => set('height', v)} /></Field>
              <Field label={t('peso.weight', 'Weight (kg)')} required><Txt value={f.weight} onChange={(v) => set('weight', v)} /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 accent-blue-600"
                checked={f.outside_lgu}
                onChange={(e) => toggleOutside(e.target.checked)}
              />
              {t('peso.outsideLgu', {
                defaultValue: "I live outside {{muni}} (I'm from another town in Bohol)",
                muni: office?.municipality || t('peso.thisTown', 'this town'),
              })}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={t('peso.house', 'House No. / Street Village')} required><Txt value={f.address_house} onChange={(v) => set('address_house', v)} /></Field>
              <Field
                label={t('peso.barangay', 'Barangay')}
                required
                hint={f.outside_lgu && !f.municipality ? t('peso.selectMuniFirst', 'Select your municipality first') : undefined}
              >
                {f.outside_lgu
                  ? <Sel value={f.barangay} onChange={(v) => set('barangay', v)} options={getBarangaysForMunicipality(f.municipality)} />
                  : (office?.barangays?.length && f.municipality === office.municipality)
                    ? <Sel value={f.barangay} onChange={(v) => set('barangay', v)} options={office.barangays} />
                    : <Txt value={f.barangay} onChange={(v) => set('barangay', v)} />}
              </Field>
              <Field label={t('peso.municipality', 'Municipality / City')} required>
                {f.outside_lgu
                  ? <Sel value={f.municipality} onChange={setOutsideMunicipality} options={BOHOL_MUNICIPALITIES} />
                  : <Txt value={f.municipality} onChange={(v) => set('municipality', v)} />}
              </Field>
              <Field label={t('peso.province', 'Province')} required><Txt value={f.province} onChange={(v) => set('province', v)} /></Field>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={f.perm_same} onChange={(e) => togglePermSame(e.target.checked)} />
              {t('peso.permSame', 'Permanent address is the same as present address')}
            </label>
            {!f.perm_same && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={t('peso.permHouse', 'Permanent: House No. / Street')}><Txt value={f.perm_address_house} onChange={(v) => set('perm_address_house', v)} /></Field>
                <Field label={t('peso.permBarangay', 'Permanent: Barangay')}><Txt value={f.perm_barangay} onChange={(v) => set('perm_barangay', v)} /></Field>
                <Field label={t('peso.permMunicipality', 'Permanent: Municipality / City')}><Txt value={f.perm_municipality} onChange={(v) => set('perm_municipality', v)} /></Field>
                <Field label={t('peso.permProvince', 'Permanent: Province')}><Txt value={f.perm_province} onChange={(v) => set('perm_province', v)} /></Field>
              </div>
            )}

            <Field label={t('peso.disability', 'Disability (if any)')}>
              <CheckGroup value={f.disability} onChange={(v) => set('disability', v)} options={NSRP_DISABILITIES} columns={3} />
            </Field>

            {/* Employment Status sub-block (folded into §I per NSRP REV 3) */}
            <h4 className="pt-2 text-sm font-bold text-slate-800">{t('peso.employmentSubheading', 'Employment Status')}</h4>
            <Field label={t('peso.empStatus', 'Employment Status')} required><RadioRow name="emp" value={f.employment_status} onChange={(v) => set('employment_status', v)} options={NSRP_EMPLOYMENT_STATUS} /></Field>
            <Field label={t('peso.separation', 'If unemployed, reason')}>
              <CheckGroup value={f.separation_reasons} onChange={(v) => set('separation_reasons', v)} options={NSRP_SEPARATION_REASONS} />
            </Field>
            {f.separation_reasons.includes('terminated_abroad') && (
              <Field label={t('peso.terminatedAbroadCountry', 'Terminated/Laid off abroad — country')}><Txt value={f.terminated_abroad_country} onChange={(v) => set('terminated_abroad_country', v)} /></Field>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={t('peso.activelyLooking', 'Actively looking for work?')} required><RadioRow name="actively" value={f.actively_looking} onChange={(v) => set('actively_looking', v)} options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} /></Field>
              {f.actively_looking !== 'no' && <Field label={t('peso.jobSearch', 'How long have you been looking for work?')}><Txt value={f.job_search_months} onChange={(v) => set('job_search_months', v)} placeholder="e.g. 6 months" /></Field>}
              <Field label={t('peso.willing', 'Willing to work immediately?')}><RadioRow name="willing" value={f.willing_immediately} onChange={(v) => set('willing_immediately', v)} options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} /></Field>
              {f.willing_immediately === 'no' && <Field label={t('peso.willingWhen', 'If no, when?')}><Txt value={f.willing_when} onChange={(v) => set('willing_when', v)} /></Field>}
              <Field label={t('peso.fourps', 'Are you a 4Ps beneficiary?')}><RadioRow name="fourps" value={f.fourps} onChange={(v) => set('fourps', v)} options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} /></Field>
              {f.fourps === 'yes' && <Field label={t('peso.fourpsId', 'Household ID No.')}><Txt value={f.fourps_household_id} onChange={(v) => set('fourps_household_id', v)} /></Field>}
              <Field label={t('peso.ofw', 'Are you an OFW?')}><RadioRow name="ofw" value={f.ofw} onChange={(v) => set('ofw', v)} options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} /></Field>
              <Field label={t('peso.consideringReturn', 'Considering coming back to the Philippines?')}><RadioRow name="consret" value={f.considering_return} onChange={(v) => set('considering_return', v)} options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} /></Field>
            </div>
          </SectionCard>

          {/* II. Job Preference */}
          <SectionCard title={t('peso.prefTitle', 'II. Job Preference')}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t('peso.occ1', 'Preferred Occupation 1')} hint={t('peso.occHint', 'e.g., clerk, call center agent, saleslady')}><Txt value={f.pref_occupation_1} onChange={(v) => set('pref_occupation_1', v)} /></Field>
              <Field label={t('peso.ind1', 'Industry 1')} hint={t('peso.indHint', 'e.g., IT-BPM, Construction, Manufacturing')}><Txt value={f.pref_industry_1} onChange={(v) => set('pref_industry_1', v)} /></Field>
              <Field label={t('peso.occ2', 'Preferred Occupation 2')}><Txt value={f.pref_occupation_2} onChange={(v) => set('pref_occupation_2', v)} /></Field>
              <Field label={t('peso.ind2', 'Industry 2')}><Txt value={f.pref_industry_2} onChange={(v) => set('pref_industry_2', v)} /></Field>
              <Field label={t('peso.occ3', 'Preferred Occupation 3')}><Txt value={f.pref_occupation_3} onChange={(v) => set('pref_occupation_3', v)} /></Field>
              <Field label={t('peso.ind3', 'Industry 3')}><Txt value={f.pref_industry_3} onChange={(v) => set('pref_industry_3', v)} /></Field>
            </div>
            <Field label={t('peso.prefType', 'Employment type (not part of the official form)')}><RadioRow name="ptype" value={f.pref_employment_type} onChange={(v) => set('pref_employment_type', v)} options={NSRP_EMPLOYMENT_TYPE_PREF} /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label={t('peso.local1', 'Preferred Local Location 1')}><Txt value={f.pref_local_1} onChange={(v) => set('pref_local_1', v)} /></Field>
              <Field label={t('peso.local2', 'Local 2')}><Txt value={f.pref_local_2} onChange={(v) => set('pref_local_2', v)} /></Field>
              <Field label={t('peso.local3', 'Local 3')}><Txt value={f.pref_local_3} onChange={(v) => set('pref_local_3', v)} /></Field>
              <Field label={t('peso.overseas1', 'Preferred Overseas 1')}><Txt value={f.pref_overseas_1} onChange={(v) => set('pref_overseas_1', v)} /></Field>
              <Field label={t('peso.overseas2', 'Overseas 2')}><Txt value={f.pref_overseas_2} onChange={(v) => set('pref_overseas_2', v)} /></Field>
              <Field label={t('peso.overseas3', 'Overseas 3')}><Txt value={f.pref_overseas_3} onChange={(v) => set('pref_overseas_3', v)} /></Field>
            </div>
            <Field label={t('peso.salaryExpectation', 'Salary Expectation (PHP)')}><Txt type="number" value={f.salary_expectation} onChange={(v) => set('salary_expectation', v)} /></Field>
          </SectionCard>

          {/* III. Educational Background */}
          <SectionCard title={t('peso.eduTitle', 'III. Educational Background')}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={t('peso.currentlyInSchool', 'Currently in school?')}><RadioRow name="cis" value={f.currently_in_school} onChange={(v) => set('currently_in_school', v)} options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} /></Field>
              <Field label={t('peso.highestEducation', 'Highest Educational Level')} required><Sel value={f.highest_education} onChange={(v) => set('highest_education', v)} options={NSRP_HIGHEST_EDUCATION} /></Field>
            </div>
            {f.education_rows.map((row, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t('peso.eduLevel', 'Level')}><Sel value={row.level} onChange={(v) => setRow('education_rows', i, 'level', v)} options={NSRP_EDUCATION_LEVELS} /></Field>
                  {row.level === 'senior_high' && <Field label={t('peso.strand', 'Senior High Strand')}><Sel value={row.strand} onChange={(v) => setRow('education_rows', i, 'strand', v)} options={SENIOR_HIGH_STRANDS} /></Field>}
                  <Field label={t('peso.school', 'School / University')}><Txt value={row.school} onChange={(v) => setRow('education_rows', i, 'school', v)} /></Field>
                  <Field label={t('peso.course', 'Course / Program')}><Txt value={row.course} onChange={(v) => setRow('education_rows', i, 'course', v)} /></Field>
                  <Field label={t('peso.yearGrad', 'Year Graduated')}><Txt type="number" value={row.year_graduated} onChange={(v) => setRow('education_rows', i, 'year_graduated', v)} /></Field>
                  <Field label={t('peso.yearLast', 'Year Last Attended (if undergrad)')}><Txt type="number" value={row.year_last} onChange={(v) => setRow('education_rows', i, 'year_last', v)} /></Field>
                  <Field label={t('peso.awards', 'Awards / Honors Received')}><Txt value={row.awards} onChange={(v) => setRow('education_rows', i, 'awards', v)} /></Field>
                </div>
                {f.education_rows.length > 1 && (
                  <button onClick={() => rmRow('education_rows', i)} className="inline-flex items-center gap-1 text-xs text-red-500"><Trash2 size={12} /> {t('peso.remove', 'Remove')}</button>
                )}
              </div>
            ))}
            <button onClick={() => addRow('education_rows', emptyEdu)} className="inline-flex items-center gap-1 text-sm font-medium text-blue-700"><Plus size={14} /> {t('peso.addEdu', 'Add education row')}</button>
          </SectionCard>

          {/* IV. Technical / Vocational Training */}
          <SectionCard title={t('peso.trainTitle', 'IV. Technical/Vocational and Other Training')}>
            <Field label={t('peso.currentlyInTraining', 'Currently in training?')}><RadioRow name="cit" value={f.currently_in_training} onChange={(v) => set('currently_in_training', v)} options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} /></Field>
            {f.training_rows.map((row, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t('peso.trainCourse', 'Training')}><Txt value={row.course} onChange={(v) => setRow('training_rows', i, 'course', v)} /></Field>
                  <Field label={t('peso.trainInst', 'Training Institution')}><Txt value={row.institution} onChange={(v) => setRow('training_rows', i, 'institution', v)} /></Field>
                  <Field label={t('peso.trainFrom', 'Duration: From')}><Txt type="date" value={row.date_from} onChange={(v) => setRow('training_rows', i, 'date_from', v)} /></Field>
                  <Field label={t('peso.trainTo', 'Duration: To')}><Txt type="date" value={row.date_to} onChange={(v) => setRow('training_rows', i, 'date_to', v)} /></Field>
                  <Field label={t('peso.trainHours', 'Hours')}><Txt type="number" value={row.hours} onChange={(v) => setRow('training_rows', i, 'hours', v)} /></Field>
                  <Field label={t('peso.trainCert', 'Certificates Received')}><Sel value={row.cert} onChange={(v) => setRow('training_rows', i, 'cert', v)} options={NSRP_NC_CERTS} /></Field>
                  <Field label={t('peso.trainCompleted', 'Completed?')}><RadioRow name={`tc${i}`} value={row.completed} onChange={(v) => setRow('training_rows', i, 'completed', v)} options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} /></Field>
                </div>
                {f.training_rows.length > 1 && (
                  <button onClick={() => rmRow('training_rows', i)} className="inline-flex items-center gap-1 text-xs text-red-500"><Trash2 size={12} /> {t('peso.remove', 'Remove')}</button>
                )}
              </div>
            ))}
            <button onClick={() => addRow('training_rows', emptyTraining)} className="inline-flex items-center gap-1 text-sm font-medium text-blue-700"><Plus size={14} /> {t('peso.addTrain', 'Add training row')}</button>
          </SectionCard>

          {/* V. Eligibility (incl. Language Proficiency Cert + Dialects) */}
          <SectionCard title={t('peso.eligTitle', 'V. Eligibility')}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label={t('peso.csEligibility', 'Career Service / Board / Bar')}><Txt value={f.civil_service_eligibility} onChange={(v) => set('civil_service_eligibility', v)} /></Field>
              <Field label={t('peso.prc', 'License Number')}><Txt value={f.prc_license} onChange={(v) => set('prc_license', v)} /></Field>
              <Field label={t('peso.prcValid', 'Expiry Date')}><Txt type="date" value={f.prc_valid_until} onChange={(v) => set('prc_valid_until', v)} /></Field>
            </div>

            <h4 className="pt-2 text-sm font-bold text-slate-800">{t('peso.langTitle', 'Language Proficiency & Dialects')}</h4>
            <Field label={t('peso.langCerts', 'Language Proficiency Certification (if any)')}>
              <CheckGroup value={f.lang_certs} onChange={(v) => set('lang_certs', v)} options={NSRP_LANG_CERTS} columns={3} />
            </Field>
            {f.lang_certs.includes('Others') && (
              <Field label={t('peso.langCertOther', 'Other certification (specify)')}><Txt value={f.lang_cert_other} onChange={(v) => set('lang_cert_other', v)} /></Field>
            )}
            {f.lang_certs.length > 0 && (
              <Field label={t('peso.langValidity', 'Validity date')}><Txt type="date" value={f.lang_validity} onChange={(v) => set('lang_validity', v)} /></Field>
            )}
            <Field label={t('peso.dialects', 'Dialects Spoken')}>
              <CheckGroup value={f.dialects} onChange={(v) => set('dialects', v)} options={NSRP_DIALECTS} columns={3} />
            </Field>
            {f.dialects.includes('Others') && (
              <Field label={t('peso.dialectOther', 'Other dialect (specify)')}><Txt value={f.dialect_other} onChange={(v) => set('dialect_other', v)} /></Field>
            )}
          </SectionCard>

          {/* VI. Work Experience */}
          <SectionCard title={t('peso.workTitle', 'VI. Work Experience (last 10 years, most recent first)')}>
            {f.work_rows.map((row, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t('peso.company', 'Name of Office/Company')}><Txt value={row.company} onChange={(v) => setRow('work_rows', i, 'company', v)} /></Field>
                  <Field label={t('peso.workAddress', 'Address')}><Txt value={row.city} onChange={(v) => setRow('work_rows', i, 'city', v)} /></Field>
                  <Field label={t('peso.position', 'Position Held')}><Txt value={row.position} onChange={(v) => setRow('work_rows', i, 'position', v)} /></Field>
                  <Field label={t('peso.inclFrom', 'Inclusive Dates — From (mm/yyyy)')}><Txt type="month" value={row.date_from} onChange={(v) => setRow('work_rows', i, 'date_from', v)} /></Field>
                  <Field label={t('peso.inclTo', 'Inclusive Dates — To (mm/yyyy)')}><Txt type="month" value={row.date_to} onChange={(v) => setRow('work_rows', i, 'date_to', v)} /></Field>
                  <Field label={t('peso.appointment', 'Status of Appointment')}><Sel value={row.appointment_status} onChange={(v) => setRow('work_rows', i, 'appointment_status', v)} options={NSRP_APPOINTMENT_STATUS} /></Field>
                </div>
                {f.work_rows.length > 1 && (
                  <button onClick={() => rmRow('work_rows', i)} className="inline-flex items-center gap-1 text-xs text-red-500"><Trash2 size={12} /> {t('peso.remove', 'Remove')}</button>
                )}
              </div>
            ))}
            <button onClick={() => addRow('work_rows', emptyWork)} className="inline-flex items-center gap-1 text-sm font-medium text-blue-700"><Plus size={14} /> {t('peso.addWork', 'Add work experience')}</button>
            <Field label={t('peso.yoe', 'Total years of experience (for job matching — not part of the official form)')} required><Sel value={f.years_of_experience} onChange={(v) => set('years_of_experience', v)} options={YEARS_OF_EXPERIENCE} /></Field>
          </SectionCard>

          {/* 21st Century Skills */}
          <SectionCard title={t('peso.c21Title', 'VII. 21st Century Skills (check five you possess)')}>
            <Field label={t('peso.c21', 'Select up to 5 skills')} hint={t('peso.c21Hint', 'Self-assessment — choose five')}>
              <CheckGroup
                value={f.skills_21}
                onChange={(v) => set('skills_21', v.length <= 5 ? v : f.skills_21)}
                options={NSRP_21C_SKILLS}
                columns={3}
              />
            </Field>
          </SectionCard>

          {/* VIII. Technical Skills Acquired Without Formal Training */}
          <SectionCard title={t('peso.techSkillsTitle', 'VIII. Technical Skills Acquired Without Formal Training')}>
            <Field label={t('peso.otherSkills', 'Select the skills you have (no certificate required)')} required>
              <CheckGroup value={f.other_skills} onChange={(v) => set('other_skills', v)} options={NSRP_OTHER_SKILLS} columns={3} />
            </Field>
            {f.other_skills.includes('Others') && (
              <Field label={t('peso.techSkillsOther', 'Other technical skill (specify)')}><Txt value={f.other_skills_other} onChange={(v) => set('other_skills_other', v)} /></Field>
            )}
          </SectionCard>

          {/* Skills (for job matching) — not part of the official form */}
          <SectionCard title={t('peso.skillsTitle', 'Skills (for job matching, not part of the official form)')}>
            <Field label={t('peso.skills', 'Your skills')} hint={t('peso.skillsHint', 'Up to 12 — drives automated job matching')}>
              <SkillsSelector options={WORKER_SKILLS} value={f.skills} onChange={(v) => set('skills', v)} max={12} />
            </Field>
          </SectionCard>

          {/* Certification & Consent */}
          <SectionCard title={t('peso.certTitle', 'Certification')}>
            <p className="text-sm text-slate-600">
              {t('peso.reviewSummaryOnePage', 'Please review your entries above. By submitting, you certify that the information is true and correct.')}
            </p>
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
              <p><strong>{t('peso.name', 'Name')}:</strong> {[f.first_name, f.middle_name, f.surname, f.suffix].filter(Boolean).join(' ')}</p>
              <p><strong>{t('peso.contact', 'Contact')}:</strong> {f.phone} · {f.email}</p>
              <p><strong>{t('peso.address', 'Address')}:</strong> {[f.address_house, f.barangay, f.municipality, f.province].filter(Boolean).join(', ')}</p>
              <p><strong>{t('peso.skills', 'Skills')}:</strong> {f.skills.join(', ') || '—'}</p>
            </div>
            <label className="flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-slate-700">
              <input type="checkbox" checked={f.consent} onChange={(e) => set('consent', e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" />
              <span>
                {t('peso.consentAgreePrefix', 'I have read and agree to the')}{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline font-medium">{t('peso.termsOfService', 'Terms of Service')}</a>
                {' '}{t('common.and', 'and')}{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline font-medium">{t('peso.privacyPolicy', 'Privacy Policy')}</a>.{' '}
                {t('peso.consent', 'I certify the information is true and correct, and I consent to PESO Panglao and Bohol Trabaho collecting and processing my data (including for DOLE reporting) under the Data Privacy Act of 2012 and the LGU–Bohol Trabaho MOA.')}
              </span>
            </label>
          </SectionCard>
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={handleSubmit} disabled={submitting || !f.consent} className="bg-blue-700 hover:bg-blue-800">
            {submitting ? <><Loader2 size={16} className="mr-1 animate-spin" /> {t('peso.submitting', 'Submitting…')}</> : t('peso.submit', 'Submit NSRP Registration')}
          </Button>
        </div>
      </PesoLayout>
    </>
  );
};

export default PesoSeekerRegister;
